import React, { useState, useEffect, useRef } from 'react';
import { Modal, ButtonGroup } from "reactstrap";
import { CustomButton } from "../home/main/home-layout-draggable-columns/custom-button";
import { fetchForAllProducts } from "../../api";
import { useTranslation } from "react-i18next";

import  Product  from "./items/Product.jsx"

import "./items/modalCSS.css"

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

const mergeProductWithCatalog = (savedProduct, catalog = []) => {
  if (!savedProduct || typeof savedProduct !== "object") return savedProduct;
  const fresh = catalog.find((p) => p?.id === savedProduct.id);
  const images =
    fresh?.images ||
    fresh?.image ||
    savedProduct?.images ||
    savedProduct?.image;

  if (!fresh) {
    return images ? { ...savedProduct, images, image: images } : savedProduct;
  }

  return {
    ...savedProduct,
    name: savedProduct.name || fresh.name || fresh.title,
    price: savedProduct.price ?? fresh.price,
    images,
    image: images,
  };
};

const availableProductsExcludingCart = (catalog = [], cart = []) => {
  const cartIds = new Set(cart.map((item) => item?.id).filter(Boolean));
  return catalog.filter((ap) => ap && !cartIds.has(ap.id));
};

export const ModalProducts = ({ 
  isOpen,
  onClose,
  onDone,
  availableProducts,
  selectedProducts,
  label,
  isModalNotification,
  selectOneItem,
  storeKey
}) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const catalogSnapshotRef = useRef(null);

  const catalog = availableProducts ?? [];

  // Solo al abrir o si cambia la selección guardada en el padre
  useEffect(() => {
    if (!isOpen) {
      catalogSnapshotRef.current = null;
      return;
    }
    catalogSnapshotRef.current = catalog;
    setCartItems(
      (selectedProducts ?? []).map((item) => mergeProductWithCatalog(item, catalog))
    );
  }, [isOpen, selectedProducts]);

  // Catálogo refrescado con el modal abierto: enriquecer carrito en sesión, sin reset
  useEffect(() => {
    if (!isOpen) return;
    if (catalogSnapshotRef.current === catalog) return;
    catalogSnapshotRef.current = catalog;
    setCartItems((items) =>
      items.map((item) => mergeProductWithCatalog(item, catalog))
    );
  }, [catalog, isOpen]);

  // Lista izquierda = catálogo menos lo que está en el carrito actual
  useEffect(() => {
    if (!isOpen) return;
    setProducts(availableProductsExcludingCart(catalog, cartItems));
  }, [catalog, cartItems, isOpen]);

  useEffect(() => {
    if (isModalNotification == true) {
      fetchData();
    }
  }, [isModalNotification]);

  // Fetch Products for Notification Modal
  const fetchData = async () => {
    if (storeKey) {
      const response = await fetchForAllProducts({ storeKey: storeKey });
      const filteredProducts = response.data.filter((p) => {
        const foundIndex = selectedProducts.findIndex((sp) => sp.id === p.id);
        return foundIndex === -1; // Exclude products found in selectedProducts
      });
      setProducts(filteredProducts);
    }
  };

  const handleAddToCart = (product) => {
    if (!selectOneItem || (selectOneItem && cartItems.length === 0)) {
      const foundProduct = products.find((p) => p.id === product.id);
      if (foundProduct) {
        setCartItems([...cartItems, foundProduct]);
      }
    }
  };

  const handleRemoveFromCart = (productId) => {
    setCartItems(cartItems.filter((item) => item.id !== productId));
  };

  const handleAddAllToCart = () => {
    setCartItems([...cartItems, ...products]);
  };

  const handleRemoveAllFromCart = () => {
    setCartItems([]);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const secondHandleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setCartItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      className="transitionSelector preview previewModal isOpen"
      size="xl"
    >
      

      <section>
        <div className="container-modal-product">
          <div className="left_container">
            <div className="product_container">
              <h2 className="">{t("homeModalProducts.title")}</h2>
              <div className="products">
                <div className="product_list">
                  {products &&
                    products.map((product) => (
                      <Product
                        key={product.id}
                        id={product.id}
                        name={product?.name || product?.title || "Unnamed product"}
                        price={product?.price}
                        image={product?.images || product?.image}
                        onAddToCart={handleAddToCart}
                        onRemoveFromCart={null} // Disable remove button in the products list
                      ></Product>
                    ))}
                </div>
              </div>

              {/* {products.length > 0 && ( */}
              <div className="bottom_product_container">
                <h3 className="product_found">
                  {products ? products.length : 0} {t("homeModalProducts.productsFound")}
                </h3>
                <h3 className="addToCart" onClick={handleAddAllToCart}>
                {t("homeModalProducts.addThemAll")}
                </h3>
              </div>
              {/* )} */}
            </div>
          </div>
          <div className="right_container">
            <div className="item_container">
              <div className="right_container_btn">
                <ButtonGroup>
                  <CustomButton
                    type="CANCEL"
                    label={t("homeModalProducts.buttonCancel")}
                    onClick={onClose}
                  />
                  <button
                type="button"
                className="close__button custom-button save-modal-button"
                onClick={() => {
                  onDone({
                    label,
                    configuration: {
                      type: "supermodal",
                      products: cartItems,
                    },
                  });
                }}
              >
                {t("homeModalProducts.buttonUpdate")} 
              </button>
                </ButtonGroup>
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={secondHandleDragEnd}
              >
                <SortableContext
                  items={cartItems || []}
                  strategy={rectSortingStrategy}
                >
                  <div className="products">
                    <div className="product_list">
                      {cartItems &&
                        cartItems.map((item) => {
                          if (!item || typeof item !== "object" || !item.id) {
                            return null;
                          }

                          return (
                            <Product
                              key={item.id}
                              id={item.id}
                              name={item?.name || item?.title || "Unnamed product"}
                              price={item?.price}
                              image={item?.images || item?.image}
                              onAddToCart={null}
                              onRemoveFromCart={() =>
                                handleRemoveFromCart(item.id)
                              }
                            ></Product>
                          );
                        })}
                    </div>
                  </div>
                </SortableContext>
              </DndContext>

              <div className="bottom_product_container">
                <h3 className="product_found">
                  {cartItems ? cartItems.length : 0} {t("homeModalProducts.productsFound")}
                </h3>
                <h3 className="removeFromCart" onClick={handleRemoveAllFromCart}>
                {t("homeModalProducts.buttonClear")}
                </h3>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Modal>
  );
};

export default ModalProducts;
